import type { EditorRootState } from "@v1/store/store";
import type { ComponentTemplate } from "@v1/types/component-template";
import type { componentTemplatePreviewPropsSchema } from "schemas/element";
import type {
  ComponentTemplateScope,
  ComponentTemplateType,
} from "schemas/generated/componentTemplates";
import type z from "zod";

import { getDependencyData } from "@v1/actions/core-actions";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import useGetDesignLibraryComponentReferences from "@v1/hooks/designLibrary/useGetDesignLibraryComponentReferences";
import useGetStoreNameAndUrl from "@v1/hooks/useGetStoreNameAndUrl";
import {
  selectDraftComponent,
  selectDraftComponentNodeFromActiveCanvas,
  selectDraftParentComponent,
  selectGetAttribute,
  selectProjectId,
} from "@v1/reducers/core-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import {
  componentTemplatesRouter,
  componentTemplatesUtils,
} from "@v1/trpc/routes/componentTemplates";
import { isFullWidthComponent } from "@v1/utils/component-attribute";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { refreshComponentIds } from "replo-runtime/shared/refreshComponentIds";

function useDraftComponentWithData() {
  const component = useEditorSelector(selectDraftComponent);
  const parent = useEditorSelector(selectDraftParentComponent);
  const getAttribute = useEditorSelector(selectGetAttribute);
  const draftComponentNode = useEditorSelector(
    selectDraftComponentNodeFromActiveCanvas,
  );

  return {
    component,
    node: draftComponentNode,
    isFullWidth: isFullWidthComponent(component, getAttribute, parent),
  };
}

const createTemplatePreviewProps = (
  state: EditorRootState,
  storeShopifyUrl: string,
): z.infer<typeof componentTemplatePreviewPropsSchema> | null => {
  const publishData = getDependencyData(state);
  const projectId = selectProjectId(state);

  if (publishData.elementId === null || !projectId) {
    return null;
  }

  return {
    ...publishData,
    projectId,
    storeUrl: storeShopifyUrl,
    elementProducts: publishData.products,
    currencyCode: state.commerce.activeCurrency,
    activeLanguage: state.commerce.activeLanguage,
    activeShopifyUrlRoot: state.commerce.shopifyUrlRoot,
    moneyFormat: state.commerce.moneyFormat,
    moneyWithCurrencyFormat: state.commerce.moneyWithCurrencyFormat,
  };
};

export const useSaveComponentTemplate = (
  onSuccess?: () => void,
): {
  isLoading: boolean;
  handleSubmit: (
    name: string,
    selectedType: ComponentTemplateType,
  ) => Promise<void>;
} => {
  const projectId = useCurrentProjectId();
  const store = useEditorStore();
  const { storeUrl } = useGetStoreNameAndUrl();
  const { component: draftComponent } = useDraftComponentWithData();
  const logEvent = useLogAnalytics();
  const { getDesignLibraryComponentReferences } =
    useGetDesignLibraryComponentReferences();
  const draftComponentNode = useEditorSelector(
    selectDraftComponentNodeFromActiveCanvas,
  );

  const { mutateAsync: createComponentTemplateV2, isPending: isLoading } =
    componentTemplatesRouter.createComponentTemplateV2.useMutation({
      onSuccess: (_, variables) => {
        handleComponentTemplateSuccess(
          (variables as { componentTemplate: Omit<ComponentTemplate, "id"> })
            .componentTemplate,
        );
        void componentTemplatesUtils.find.invalidate();
      },
    });

  function handleComponentTemplateSuccess(
    componentTemplate: Omit<ComponentTemplate, "id">,
  ) {
    logEvent("editor.componentTemplate.save", {
      scope: "store",
      type: componentTemplate.type,
      name: componentTemplate.name,
    });

    if (onSuccess) {
      onSuccess();
    }

    const componentTemplateTypeName =
      componentTemplate.type.slice(0, 1).toUpperCase() +
      componentTemplate.type.slice(1);
    const SavedLocation =
      componentTemplate.type === "page" ? "template library" : "insert panel";

    successToast(
      `${componentTemplateTypeName} saved`,
      `Saved ${componentTemplateTypeName}s can be found in the ${SavedLocation}.`,
    );
  }

  const handleSubmit = async (
    name: string,
    selectedType: ComponentTemplateType,
  ) => {
    if (draftComponent) {
      const generateCreateComponentTemplatePayload = () => {
        const designLibraryMetadata = getDesignLibraryComponentReferences([
          draftComponent.id,
        ]);
        const newComponent = refreshComponentIds(draftComponent).component;
        const draftComponentNodeBoundingClientRect =
          draftComponentNode?.getBoundingClientRect();

        const payload: any = {
          componentTemplate: {
            name,
            type: selectedType,
            scope: "store" as ComponentTemplateScope,
            storeId: projectId ?? "",
            template: newComponent,
            categoryId: null,
            designLibraryMetadata,
          },
          storeId: projectId ?? "",
        };

        if (draftComponentNodeBoundingClientRect) {
          payload.componentDimensions = {
            width: draftComponentNodeBoundingClientRect.width,
            height: draftComponentNodeBoundingClientRect.height,
          };
        }

        return payload;
      };

      const previewData = createTemplatePreviewProps(
        store.getState(),
        storeUrl ?? "",
      );
      if (previewData) {
        await createComponentTemplateV2({
          ...generateCreateComponentTemplatePayload(),
          previewRenderProps: previewData,
        });
      }
    }
  };

  return {
    isLoading,
    handleSubmit,
  };
};
